import axios from "axios"
import bowser from "bowser"

import * as environment from "@/services/environment.js"
import { generateUUID } from "@/services/texts.js"

import { useUserSessionStore } from "@/store/userSession.js"
import { useConfigurationStore } from "@/store/configuration.js"

import routes from "@/routes.js"

import { differenceInMilliseconds } from "date-fns"

const BATCH_UPLOAD_TIMER_INTERVAL = 15000
const CLEAR_CACHE_TIME = 1000 * 60 * 5

let batchUploadTimerId = null
let analyticsEventBatch = []
const analyticsCache = {
  folderView: []
}

export const getAnalyticsFeedKeyForRoute = (route) => {
  const userSessionStore = useUserSessionStore()

  switch (route.name) {
    case routes.POST:
      return route.params.postId
    case routes.PROFILE:
      return route.params.id || userSessionStore.user?.id
    case routes.GROUPS:
      return route.params.groupId || "activity_feed"
    case routes.HASHTAGS:
      return route.params.hashtagId
  }
}

export const getAnalyticsFeedTypeForRoute = (route) => {
  switch (route.name) {
    case routes.GROUPS:
      return "group"
    case routes.PROFILE:
      return "profile"
    case routes.HASHTAGS:
      return "hashtag"
  }
}

export const getAnalyticsOriginTypeForRoute = (route) => {
  switch (route.name) {
    case routes.GROUPS:
      return route.params?.groupId ? "group_feed" : "activity_feed"
    case routes.PROFILE:
      return "profile_feed"
    case routes.HASHTAGS:
      return "hashtag_feed"
    case routes.POST:
      return "post_details"
  }
}

export const registerAnalyticsEvent = (analyticsEvent) => {
  if (environment.isCrawler()) {
    return
  }

  if (batchUploadTimerId === null) {
    startBatchUploadTimer()
  }

  analyticsEventBatch.push({
    id: generateUUID(),
    at: new Date().valueOf(),
    ...analyticsEvent
  })
}

export const registerCacheAnalyticsEvent = (analyticsEvent, { cacheName, cacheKey }) => {
  const cache = analyticsCache[cacheName]
  const currentDate = new Date()

  const existing = cache.find((c) => c.cacheKey === cacheKey)
  if (existing) {
    if (differenceInMilliseconds(currentDate, existing.date) < CLEAR_CACHE_TIME) {
      return
    }
    existing.date = currentDate
  } else {
    cache.push({ cacheKey, date: currentDate })
  }

  registerAnalyticsEvent(analyticsEvent)
}

const startBatchUploadTimer = () => {
  batchUploadTimerId = setInterval(() => {
    if (analyticsEventBatch.length) {
      sendAnalytics()
    } else {
      stopBatchUploadTimer()
    }
  }, BATCH_UPLOAD_TIMER_INTERVAL)
}

const stopBatchUploadTimer = () => {
  clearInterval(batchUploadTimerId)
  batchUploadTimerId = null
}

const analyticsConfigValid = () => {
  if (import.meta.env.MODE !== "production" || import.meta.env.VITE_APP_E2E) {
    return false
  }
  const configurationStore = useConfigurationStore()
  const analyticsConfig = configurationStore.configuration.analytics

  const userSessionStore = useUserSessionStore()

  return analyticsConfig?.url && analyticsConfig?.id && !userSessionStore.consolePreviewMode
}

export const sendAnalytics = async () => {
  if (!analyticsConfigValid()) {
    return
  }
  try {
    const configurationStore = useConfigurationStore()
    await axios.post(configurationStore.configuration.analytics.url, getFormattedAnalyticsBatch(), { withCredentials: false })

    analyticsEventBatch = []
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Failed to send analytics data.")
  }
}

const sendAnalyticsOnLeavingPage = () => {
  if (!analyticsConfigValid()) {
    return
  }

  const analyticsData = getFormattedAnalyticsBatch()
  if (!analyticsData.events.length) {
    return
  }

  const configurationStore = useConfigurationStore()
  const analyticsDataBlob = new Blob([JSON.stringify(analyticsData)], { type: "text/plain" })
  navigator.sendBeacon(configurationStore.configuration.analytics.url, analyticsDataBlob)

  analyticsEventBatch = []
}

const browserInfo = bowser.getParser(window.navigator.userAgent).getBrowser()

const getFormattedAnalyticsBatch = () => {
  const configurationStore = useConfigurationStore()
  const userSessionStore = useUserSessionStore()
  return {
    app_id: configurationStore.configuration.analytics.id,
    app_version: import.meta.env.VITE_APP_VERSION,
    platform_name: "web",
    platform_version: `${browserInfo.name} ${browserInfo.version}`,
    user_id: userSessionStore.user?.id || null,
    events: analyticsEventBatch
  }
}

window.addEventListener("unload", sendAnalyticsOnLeavingPage, false)

// Unload is not guaranteed to be emitted on mobile devices, but visibilitychange is
window.addEventListener("visibilitychange", sendAnalyticsOnLeavingPage, false)
